import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { AdminDamageTypeDto, AdminDamageTypeGetPaginatedDto } from "@/core/api/generated/v1";

type DamageTypeAutocompleteOption = BaseAutocompleteOption<AdminDamageTypeDto>;

const damageTypeToOption = (data: AdminDamageTypeDto): DamageTypeAutocompleteOption => ({
  id: data.id!,
  title: StringHelper.join([data.name], ", ", {
    skipEmpty: true,
  }),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface DamageTypesAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<AdminDamageTypeDto> {
  searchFilters?: Partial<Omit<AdminDamageTypeGetPaginatedDto, "search" | "includeIds">>;
}

export default function DamageTypesAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: DamageTypesAutocompleteProps) {
  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={damageTypeToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.damageTypesAdminApi.apiV1AdminReferenceDataDamageTypesGetPost,
        parameters: {
          adminDamageTypeGetPaginatedDto: {
            limit: 25,
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          adminDamageTypeGetPaginatedDto: {
            ...params.adminDamageTypeGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='DamageTypes'
      placeholder='Search for damage types...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='damageType' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option.data?.description}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
